import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Fish, Plus, Trash2, Check, X, ExternalLink, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIES_OPTIONS = [
  "Humpback Whale",
  "Fin Whale",
  "Minke Whale",
  "North Atlantic Right Whale",
  "Harbor Porpoise",
  "Atlantic White-sided Dolphin",
  "Short-beaked Common Dolphin",
  "Harbor Seal",
  "Gray Seal",
  "Other",
];

const EMPTY_FORM = {
  species: "",
  sighted_at: new Date().toISOString().slice(0, 10),
  count: 1,
  notes: "",
  spotter: "",
  location_hint: "",
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-gray-100 text-gray-500",
};

export default function WhalesManager({ authHeader }) {
  const [sightings, setSightings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const fetchSightings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/whales/sightings`, authHeader);
      setSightings(res.data.sightings || []);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchSightings(); }, [fetchSightings]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.species || !form.sighted_at) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/whales/sightings`, {
        ...form,
        count: form.count ? parseInt(form.count, 10) : 1,
      }, authHeader);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchSightings();
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/whales/sightings/${id}/status`, { status }, authHeader);
    fetchSightings();
  };

  const deleteSighting = async (id) => {
    if (!window.confirm("Delete this sighting permanently?")) return;
    await axios.delete(`${API}/admin/whales/sightings/${id}`, authHeader);
    fetchSightings();
  };

  const counts = {
    all: sightings.length,
    pending: sightings.filter(s => s.status === "pending").length,
    approved: sightings.filter(s => (s.status || "approved") === "approved").length,
    rejected: sightings.filter(s => s.status === "rejected").length,
  };
  const visible = filter === "all" ? sightings : sightings.filter(s => (s.status || "approved") === filter);

  return (
    <div className="space-y-6" data-testid="whales-manager">
      {/* Header + add toggle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="font-display text-wc-forest text-lg font-semibold flex items-center gap-2">
              <Fish className="w-5 h-5" /> Whale Sightings ({sightings.length})
            </h3>
            <p className="font-body text-gray-500 text-xs mt-1">
              Guest submissions come in as <span className="font-semibold text-amber-700">pending</span>. Approve to make them public.
            </p>
          </div>
          <Button
            onClick={() => setShowForm(s => !s)}
            className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm gap-1.5"
            data-testid="whales-add-toggle"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Close" : "Add sighting"}
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 text-xs flex-wrap">
          {["all", "pending", "approved", "rejected"].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              data-testid={`whales-filter-${s}`}
              className={`px-3 py-1.5 rounded font-body capitalize transition-colors ${
                filter === s ? "bg-wc-teal text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {s} ({counts[s]})
            </button>
          ))}
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4" data-testid="whales-add-form">
          <h4 className="font-display text-wc-forest text-base font-semibold">Field note — new sighting</h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-600">Species</Label>
              <select
                value={form.species}
                onChange={(e) => setForm({ ...form, species: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal bg-white"
                required
                data-testid="whales-species-select"
              >
                <option value="">Choose…</option>
                {SPECIES_OPTIONS.map(sp => <option key={sp} value={sp}>{sp}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Date sighted</Label>
              <Input type="date" value={form.sighted_at}
                onChange={(e) => setForm({ ...form, sighted_at: e.target.value })}
                className="mt-1" required />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-600">Count <span className="text-gray-400">(optional)</span></Label>
              <Input type="number" min="1" max="100" value={form.count}
                onChange={(e) => setForm({ ...form, count: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Spotter <span className="text-gray-400">(optional)</span></Label>
              <Input value={form.spotter}
                onChange={(e) => setForm({ ...form, spotter: e.target.value })}
                placeholder="David"
                className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600">Location hint <span className="text-gray-400">(optional)</span></Label>
            <Input value={form.location_hint}
              onChange={(e) => setForm({ ...form, location_hint: e.target.value })}
              placeholder="From the Viewing Deck, heading west"
              className="mt-1" />
          </div>

          <div>
            <Label className="text-xs text-gray-600">Notes <span className="text-gray-400">(optional)</span></Label>
            <Textarea rows={3} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Small pod, feeding on the incoming tide. Three breaches in a row."
              className="mt-1" />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => { setForm(EMPTY_FORM); setShowForm(false); }}
              variant="outline" className="font-body text-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
              className="bg-wc-teal text-white hover:bg-wc-forest font-body text-sm">
              {saving ? "Saving…" : "Save sighting"}
            </Button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {visible.length === 0 ? (
          <p className="font-accent italic text-gray-500 text-sm text-center py-10">No sightings match this filter.</p>
        ) : (
          <ul className="divide-y divide-gray-200" data-testid="whales-list">
            {visible.map(s => {
              const status = s.status || "approved";
              const d = s.sighted_at ? new Date(s.sighted_at) : null;
              const pretty = d ? d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—";
              return (
                <li key={s.id} className="p-4 sm:p-5 flex gap-4 items-start" data-testid="whales-row">
                  <div className="font-body text-gray-500 text-xs uppercase tracking-widest w-24 flex-shrink-0 pt-1">
                    {pretty}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-wc-forest text-sm font-semibold">{s.species}</span>
                      {s.count > 1 && (
                        <span className="font-body text-gray-400 text-xs">× {s.count}</span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-[10px] font-body uppercase tracking-wide ${STATUS_STYLES[status] || STATUS_STYLES.approved}`}>
                        {status}
                      </span>
                      {s.submitted_by === "guest" && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-body bg-blue-50 text-blue-700">guest</span>
                      )}
                    </div>
                    {s.location_hint && (
                      <p className="font-body text-gray-600 text-xs mt-1">{s.location_hint}</p>
                    )}
                    {s.notes && (
                      <p className="font-body text-gray-700 text-sm mt-1.5 leading-relaxed">{s.notes}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {s.spotter && (
                        <span className="font-body text-gray-500 text-xs">— {s.spotter}</span>
                      )}
                      {s.spotter_email && (
                        <a href={`mailto:${s.spotter_email}`}
                          className="inline-flex items-center gap-1 font-body text-wc-teal text-xs hover:text-wc-forest">
                          <Mail className="w-3 h-3" /> {s.spotter_email}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {status !== "approved" && (
                      <button onClick={() => updateStatus(s.id, "approved")}
                        className="p-1.5 rounded hover:bg-green-50 text-green-700" title="Approve">
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {status !== "rejected" && (
                      <button onClick={() => updateStatus(s.id, "rejected")}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Reject">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => deleteSighting(s.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="font-accent italic text-gray-400 text-xs">
        <ExternalLink className="w-3 h-3 inline mr-1" />
        Community sightings from <span className="font-semibold">iNaturalist</span> show up automatically on the public page too — they don't need approval, they're already research-grade.
      </p>
    </div>
  );
}
